/**
 * ModalDetallePropiedad — Modal de detalle de propiedad scrapeada
 * Compartido entre ScrapingPortales y FichaCliente.
 * Props: p (objeto propiedad), theme (objeto de useTheme()), onClose (fn)
 * Pipeline: captacion (objeto captación CRM), onUpdated (fn tras archivar/avanzar)
 */
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import ModalWhatsApp from "./ModalWhatsApp";
import ModalEmail    from "./ModalEmail";
import api from "../services/api";
import { useDialogs } from "../context/DialogContext";
import { llamarContacto } from "../utils/gestionVentasContactos";
import PanelHistorialCaptacion from "./PanelHistorialCaptacion";
import ModalNuevaPrecaptacion from "./ModalNuevaPrecaptacion";
import usePermiso from "../hooks/usePermiso";

const IMG_PROXY_BASE = window.location.hostname === "localhost"
  ? "http://localhost:4000/api/img-proxy?url="
  : "https://vitalcasa-crm-production.up.railway.app/api/img-proxy?url=";

const proxyFoto = (url) => url ? IMG_PROXY_BASE + encodeURIComponent(url) : url;

const EXTRAS_ES = {
  air_conditioner:"Aire acondicionado", heating:"Calefacción", furnished:"Amueblado",
  balcony:"Balcón", terrace:"Terraza", garage:"Garaje", parking:"Parking",
  storage:"Trastero", pool:"Piscina", garden:"Jardín", elevator:"Ascensor",
  equiped_kitchen:"Cocina equipada", antiquity:"Antigüedad", bathrooms:"Baños",
  conservationStatus:"Estado conservación", floor:"Planta", orientation:"Orientación",
  rooms:"Habitaciones", surface:"Superficie", new_construction:"Obra nueva",
  second_hand:"Segunda mano", good_condition:"Buen estado", need_renovation:"A reformar",
  luxury:"Lujo", duplex:"Dúplex", penthouse:"Ático", studio:"Estudio",
  ground_floor:"Planta baja", alarm:"Alarma", video_intercom:"Videoportero",
  laundry:"Lavandería", pets_allowed:"Se admiten mascotas", adapted:"Adaptado minusválidos",
};
const traducirExtra = (ex) => EXTRAS_ES[ex] || ex.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase());

const TIPO_ES = {
  flat:"Piso", apartment:"Apartamento", studio:"Estudio", loft:"Loft",
  duplex:"Dúplex", penthouse:"Ático", ground_floor:"Planta baja", mezzanine:"Entresuelo",
  house:"Casa", chalet:"Chalet", detached_house:"Chalet independiente",
  semi_detached:"Adosado", terraced_house:"Adosado", bungalow:"Bungalow",
  village_house:"Casa de pueblo", country_house:"Casa rural",
  farmhouse:"Finca rústica", rural:"Finca rústica", country:"Finca rústica", cortijo:"Cortijo",
  building:"Edificio", land:"Terreno", garage:"Garaje", storage_room:"Trastero",
  office:"Oficina", premises:"Local comercial", new_construction:"Obra nueva",
};
const traducirTipo = t => {
  if (!t) return "—";
  const key = t.toLowerCase().replace(/-/g,"_");
  return TIPO_ES[key] || t.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase());
};

const fmtFecha = iso => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-ES", { day:"2-digit", month:"2-digit", year:"2-digit", hour:"2-digit", minute:"2-digit" });
};
const fmtPrecio = n => n ? new Intl.NumberFormat("es-ES").format(n) + " €" : null;

const PORTAL_COLOR = {
  idealista: "#ef4444",
  fotocasa: "#22c55e",
  pisos: "#3b82f6",
  habitaclia: "#f97316",
};

function portalLabel(portal) {
  if (!portal) return "Portal";
  const p = String(portal).toLowerCase();
  if (p === "idealista") return "Idealista";
  if (p === "fotocasa") return "Fotocasa";
  if (p === "pisos") return "Pisos.com";
  if (p === "habitaclia") return "Habitaclia";
  return portal.charAt(0).toUpperCase() + portal.slice(1);
}

function BadgeAnunciante({ tipo, agencia, item, t }) {
  const esParticular = ["particular","private"].includes((item?.tipo_anunciante||"").toLowerCase());
  const cfg = esParticular
    ? { bg:"rgba(34,197,94,0.15)", color:"#22c55e" }
    : ({ Agencia:{ bg:"rgba(59,130,246,0.15)", color:"#3b82f6" }, Promotor:{ bg:"rgba(139,92,246,0.15)", color:"#8b5cf6" } }[tipo] || { bg:"rgba(100,116,139,0.15)", color:"#94a3b8" });
  const label = esParticular
    ? (tipo === "Particular" ? "Particular" : "📍 Probable Particular")
    : (tipo === "Agencia"
      ? (agencia || item?.nombre_contacto || item?.alias_agencia || tipo)
      : (agencia || item?.nombre_contacto || tipo || "—"));
  return (
    <span style={{ display:"inline-block", padding:"2px 8px", borderRadius:20, fontSize:10, fontWeight:700, backgroundColor:cfg.bg, color:cfg.color }}>
      {label}
    </span>
  );
}

/** Teléfono repetido en N anuncios «Particular» → posible agencia encubierta. */
export function esTelefonoIdealistaPendiente(p) {
  if (!p || String(p.portal || '').toLowerCase() !== 'idealista') return false;
  const tipo = String(p.tipo_anunciante || '').toLowerCase();
  if (!['particular', 'private'].includes(tipo)) return false;
  const t = p.telefonos?.[0];
  return !t || String(t).replace(/\D/g, '').length < 9;
}

/** Particular Idealista sin teléfono — la extensión Chrome lo rellena en cola. */
export function BadgeTelefonoPendienteIdealista({ p, theme, compact = false }) {
  if (!esTelefonoIdealistaPendiente(p)) return null;
  const c = theme?.colors || {};
  if (p.captacion_id) return null;
  if (compact) {
    return (
      <div style={{
        display: "flex", alignItems: "center", gap: 6,
        padding: "4px 8px", borderRadius: 6,
        border: "1px solid rgba(239,68,68,0.45)",
        background: "rgba(239,68,68,0.12)",
      }}>
        <span style={{ fontSize: 10, fontWeight: 800, color: "#ef4444" }}>SIN TEL.</span>
      </div>
    );
  }
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "4px 10px", borderRadius: 8, fontSize: 10, fontWeight: 700,
      border: "1px solid rgba(239,68,68,0.45)", background: "rgba(239,68,68,0.12)", color: "#ef4444",
    }}>
      📞 Sin teléfono — cola extensión Chrome (Idealista)
    </span>
  );
}

export function BadgePrecaptacionAna({ p, theme, compact = false }) {
  if (!p?.captacion_id) return null;
  if (compact) {
    return (
      <span style={{ fontSize: 9, fontWeight: 800, color: "#eab308", padding: "2px 6px", borderRadius: 5, background: "rgba(234,179,8,0.15)" }}>
        ANA
      </span>
    );
  }
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "4px 10px", borderRadius: 8, fontSize: 10, fontWeight: 700,
      border: "1px solid rgba(234,179,8,0.45)", background: "rgba(234,179,8,0.12)", color: "#eab308",
    }}>
      🤖 Precaptación Ana
    </span>
  );
}

/** Normaliza datos_extras.pipeline_envios (por usuario) a una lista ordenada de fechas ISO. */
function normalizarPipelineEnvios(p) {
  let raw = p?.mi_pipeline_envios;
  if (typeof raw === "string") {
    try { raw = JSON.parse(raw); } catch { raw = null; }
  }
  const envios = Array.isArray(raw) ? raw.filter(Boolean) : [];
  return [...envios].sort((a, b) => new Date(a) - new Date(b));
}

/** Etiqueta "Pipeline" — por usuario: solo se muestra si el agente del usuario logueado ya la envió. */
export function BadgePipelineUsuario({ p, theme, compact = false }) {
  const envios = normalizarPipelineEnvios(p);
  if (!envios.length) return null;
  const veces = envios.length;
  const ultima = fmtFecha(envios[envios.length - 1]);
  const tooltip = veces > 1
    ? `Enviada a tu Pipeline ${veces} veces\n${envios.map((f) => fmtFecha(f)).join("\n")}`
    : `Enviada a tu Pipeline: ${ultima}`;
  if (compact) {
    return (
      <div
        title={tooltip}
        style={{
          display: "flex", alignItems: "center", gap: 6,
          padding: "4px 8px", borderRadius: 8,
          color: "#fff", fontWeight: 800, fontSize: 9, letterSpacing: "0.04em", textTransform: "uppercase",
          background: "linear-gradient(135deg, #6366f1, #4f46e5)",
          border: "2px solid rgba(255,255,255,0.85)",
          boxShadow: "0 3px 12px rgba(79,70,229,0.55), 0 0 0 1px rgba(79,70,229,0.25)",
        }}
      >
        ✓ Pipeline{veces > 1 ? ` ×${veces}` : ""}
      </div>
    );
  }
  return (
    <span
      title={tooltip}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "5px 14px", borderRadius: 8,
        color: "#fff", fontWeight: 800, fontSize: 12, letterSpacing: "0.06em", textTransform: "uppercase",
        background: "linear-gradient(135deg, #6366f1, #4f46e5)",
        border: "2px solid rgba(255,255,255,0.85)",
        boxShadow: "0 3px 12px rgba(79,70,229,0.55), 0 0 0 1px rgba(79,70,229,0.25)",
      }}
    >
      ✓ Pipeline{veces > 1 ? ` ×${veces}` : ""} · {ultima}
    </span>
  );
}

/** Teléfono repetido en N anuncios «Particular» → posible agencia encubierta. */
export function BadgePosibleAgencia({ p, theme, compact = false }) {
  if (!p?.posible_agencia) return null;
  const c = theme?.colors || {};
  const n = p.telefono_num_anuncios_particular || 0;
  if (compact) {
    return (
      <div style={{
        display: "flex", alignItems: "center", gap: 6,
        padding: "4px 8px", borderRadius: 6,
        border: "1px solid rgba(245,158,11,0.45)",
        background: "rgba(245,158,11,0.12)",
      }}>
        <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="1.8" aria-hidden>
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
        <div style={{ lineHeight: 1.15 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: "#f59e0b", letterSpacing: "0.02em" }}>
            POSIBLE AGENCIA
          </div>
          <div style={{ fontSize: 8, fontWeight: 600, color: c.textMuted || "#94a3b8", letterSpacing: "0.06em" }}>
            {n} ANUNCIOS · MISMO TEL.
          </div>
        </div>
      </div>
    );
  }
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "4px 10px", borderRadius: 8, fontSize: 10, fontWeight: 700,
      border: "1px solid rgba(245,158,11,0.45)", background: "rgba(245,158,11,0.12)", color: "#f59e0b",
    }}>
      ⚠ Posible agencia · {n} anuncios con el mismo teléfono (marcados como particular)
    </span>
  );
}

function ModalDetallePropiedadScraping({ p, theme, onClose, onUpdated, onFocusVincular, captacion }) {
  const t = theme.colors;
  const isDark = theme.name === "dark";
  const dialogs = useDialogs();
  const { puede } = usePermiso();
  // Permisos "Detalle de anuncio (scraping)" — solo aplican cuando el detalle
  // viene de una captación del pipeline (captacionPipeline abajo); fuera del
  // pipeline (módulo Scraping / FichaCliente) se mantiene el comportamiento actual.
  const verScrLlamar = !captacion || puede('pl_capt_scr_llamar', 'ver');
  const verScrWhatsapp = !captacion || puede('pl_capt_scr_whatsapp', 'ver');
  const verScrEmail = !captacion || puede('pl_capt_scr_email', 'ver');
  const verScrCoincidencias = !captacion || puede('pl_capt_scr_coincidencias', 'ver');
  const [display, setDisplay] = useState(p);
  const [fotoIdx,    setFotoIdx]    = useState(0);
  const [loaded,     setLoaded]     = useState(false);
  const [modalWa,    setModalWa]    = useState(false);
  const [modalEmail, setModalEmail] = useState(false);
  const [llamando,   setLlamando]   = useState(false);
  const [hermanos,   setHermanos]   = useState([]);
  const [loadingAd,  setLoadingAd]  = useState(false);
  const [loadAdError, setLoadAdError] = useState(null);
  const [accionArchivo, setAccionArchivo] = useState(null);
  const [motivoArchivo, setMotivoArchivo] = useState("");
  const [archivando, setArchivando] = useState(false);
  const [accionesEl, setAccionesEl] = useState(null);
  const [historialEl, setHistorialEl] = useState(null);
  const userPickedRef = useRef(false);
  const autoPickDoneRef = useRef(false);
  const fotos = Array.isArray(display.fotos) ? display.fotos : [];
  const car   = display.caracteristicas || {};

  const captacionPipeline = useMemo(() => {
    if (!captacion?.id) return null;
    const nombreParts = String(captacion.nombre || display.nombre_contacto || "").trim().split(/\s+/).filter(Boolean);
    return {
      ...captacion,
      telefono: captacion.telefono || display.telefonos?.[0] || null,
      nombre: captacion.nombre || nombreParts[0] || null,
      apellido: captacion.apellido || (nombreParts.length > 1 ? nombreParts.slice(1).join(" ") : null),
    };
  }, [captacion, display.nombre_contacto, display.telefonos]);

  const loadFullAd = useCallback(async (id) => {
    setLoadingAd(true);
    setLoadAdError(null);
    try {
      const { data } = await api.get(`/scraping/propiedades/${id}`);
      setDisplay(data);
      setFotoIdx(0);
      setLoaded(false);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'No se pudo cargar el anuncio';
      setLoadAdError(msg);
    } finally {
      setLoadingAd(false);
    }
  }, []);

  useEffect(() => {
    setDisplay(p);
    userPickedRef.current = false;
    autoPickDoneRef.current = false;
    setLoadAdError(null);
    // Resumen recortado de vista Grupos (legacy o caché): cargar ficha completa
    const esResumenGrupo = p?.id && p.created_at === undefined && p.caracteristicas === undefined;
    const desc = String(p?.descripcion || "").trim();
    const nFotos = Array.isArray(p?.fotos) ? p.fotos.length : (Number(p?.total_fotos) || 0);
    const descCortadaListado =
      String(p?.portal || "").toLowerCase() === "idealista" &&
      p?.id &&
      (nFotos <= 1 ||
        (desc.length >= 180 &&
          desc.length <= 420 &&
          /[A-Za-zÁÉÍÓÚáéíóúÑñ]$/.test(desc) &&
          !/[.!?…]$/.test(desc)));
    if (esResumenGrupo || descCortadaListado) loadFullAd(p.id);
  }, [p.id, loadFullAd]);

  useEffect(() => { setFotoIdx(0); setLoaded(false); }, [display.id]);
  useEffect(() => {
    if (fotos.length === 0) return;
    fotos.forEach(url => { const img = new Image(); img.src = proxyFoto(url); });
  }, [display.id, fotos.length]);

  useEffect(() => {
    if (!display?.id) { setHermanos([]); return; }
    const vinculado = display.id_propiedad_mercado || (display.mercado_num_anuncios || 0) >= 2;
    if (!vinculado) { setHermanos([]); return; }
    api.get(`/scraping/mercado/anuncios/${display.id}`)
      .then((r) => setHermanos(r.data?.items || []))
      .catch(() => setHermanos([]));
  }, [display.id, display.id_propiedad_mercado, display.mercado_num_anuncios]);

  useEffect(() => {
    if (!hermanos.length || userPickedRef.current || autoPickDoneRef.current) return;
    const particular = hermanos.find((a) =>
      ['particular', 'private'].includes(String(a.tipo_anunciante || '').toLowerCase())
    );
    autoPickDoneRef.current = true;
    if (particular && String(particular.id) !== String(display.id)) {
      loadFullAd(particular.id);
    }
  }, [hermanos, display.id, loadFullAd]);

  const selectAnuncio = (a, e) => {
    if (e?.target?.closest?.('a')) return;
    if (String(a.id) === String(display.id)) return;
    userPickedRef.current = true;
    loadFullAd(a.id);
  };

  const tel   = display.telefonos?.[0] || '';
  const telHeredado = display.telefono_heredado && display.telefono_heredado_portal;
  const telLabel = telHeredado
    ? `Teléfono (desde ${portalLabel(display.telefono_heredado_portal)})`
    : 'Teléfono';
  const email = display.emails?.[0] || '';
  const esAgenciaAnuncio = !['particular', 'private'].includes(String(display.tipo_anunciante || '').toLowerCase());
  const nombre = display.alias_agencia || display.nombre_contacto || '';

  const contactoModal = useMemo(() => {
    const nom = nombre.trim() || 'Contacto';
    const parts = nom.split(/\s+/).filter(Boolean);
    return {
      nombre: nom,
      telefono: tel,
      email,
      iniciales: parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : (parts[0]?.[0] || 'C').toUpperCase(),
    };
  }, [nombre, tel, email]);

  const ejecutarLlamada = useCallback(async () => {
    if (!tel || llamando) return;
    setLlamando(true);
    try {
      await llamarContacto({ telefono: tel, nombre: contactoModal.nombre });
      await dialogs.alert("📞 Llamada iniciada — tu móvil recibirá la llamada en breve", { autoCloseMs: 2000 });
    } catch (e) {
      await dialogs.alert(e.message || "Error al llamar");
    } finally {
      setLlamando(false);
    }
  }, [tel, llamando, contactoModal.nombre, dialogs]);

  const handleArchivar = async () => {
    if (!captacionPipeline?.id || !accionArchivo) return;
    setArchivando(true);
    try {
      const motivo = String(motivoArchivo || "").trim() || null;
      await api.patch(`/captaciones/${captacionPipeline.id}/etapa`, {
        etapa: accionArchivo,
        motivo,
        historial_data: {
          tipo: "cambio_etapa",
          contenido: accionArchivo === "fria"
            ? (motivo || "Enviada a Fríos")
            : (motivo || "Archivada en Eliminados"),
          metadata: {
            etapa_origen: captacionPipeline.etapa || "precaptacion",
            motivo: motivo || null,
          },
        },
      });
      setAccionArchivo(null);
      setMotivoArchivo("");
      onUpdated?.();
      onClose?.();
    } catch (e) {
      await dialogs.alert(e.response?.data?.error || "Error al archivar la precaptación");
    } finally {
      setArchivando(false);
    }
  };

  const inputArchivoStyle = {
    width: "100%",
    padding: "8px 12px",
    fontSize: 12,
    border: `1px solid ${t.border}`,
    borderRadius: 8,
    background: t.bgInput,
    color: t.textPrimary,
    outline: "none",
    fontFamily: "inherit",
    resize: "none",
    boxSizing: "border-box",
    lineHeight: 1.5,
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, backgroundColor: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}>
      <div style={{ position: 'relative', width: '100%', maxWidth: 1320, maxHeight: '92vh', borderRadius: 16, backgroundColor: t.bgCard, color: t.textPrimary, boxShadow: '0 24px 60px rgba(0,0,0,0.5)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}>

        {/* Cabecera */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: `1px solid ${t.border}`, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20 }}>🏠</span>
            <div>
              <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>{display.direccion || display.titulo || 'Sin dirección'}</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                <BadgeAnunciante tipo={display.tipo_anunciante} agencia={display.alias_agencia} item={display} t={t} />
                <BadgePosibleAgencia p={display} theme={theme} />
                <BadgeTelefonoPendienteIdealista p={display} theme={theme} />
                <BadgePrecaptacionAna p={display} theme={theme} />
                <BadgePipelineUsuario p={display} theme={theme} />
                {display.portal && (
                  <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600, backgroundColor: 'rgba(249,115,22,0.15)', color: '#f97316', textTransform: 'capitalize' }}>
                    {display.portal}
                  </span>
                )}
                {display.fecha_publicacion && (
                  <span style={{ fontSize: 11, color: t.textMuted }}>Publicado: {fmtFecha(display.fecha_publicacion)}</span>
                )}
                <span style={{ fontSize: 11, color: t.textMuted }}>Importado: {fmtFecha(display.created_at)}</span>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {display.url_original && (
              <a href={display.url_original} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${t.border}`, backgroundColor: 'transparent', color: '#ffffff', fontSize: 12, fontWeight: 600, textDecoration: 'none', cursor: 'pointer' }}>
                Ver anuncio ↗
              </a>
            )}
            <button onClick={onClose} style={{ padding: '6px 10px', borderRadius: 8, border: `1px solid ${t.border}`, backgroundColor: 'transparent', color: t.textMuted, cursor: 'pointer', fontSize: 14 }}>✕</button>
          </div>
        </div>

        {/* Cuerpo: dos mitades iguales. Izquierda = fotos + descripción.
            Derecha = precio + portales (arriba) + contacto + acciones rápidas + datos + Historial (abajo, con scroll propio) + acciones. */}
        <div style={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative' }}>
        {loadingAd && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 2, backgroundColor: 'rgba(0,0,0,0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: '#fff',
          }}>Cargando anuncio…</div>
        )}
        {loadAdError && (
          <div style={{
            padding: '8px 20px', backgroundColor: 'rgba(239,68,68,0.15)', color: '#fca5a5',
            fontSize: 12, borderBottom: `1px solid ${t.border}`, flexShrink: 0,
          }}>
            ⚠ {loadAdError}
          </div>
        )}
        <div style={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden', display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: 'minmax(0, 1fr)' }}>

          {/* Col izquierda: fotos + descripción (con scroll propio de respaldo si no cabe) */}
          <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0, overflow: 'auto', borderRight: `1px solid ${t.border}` }}>
            {fotos.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ position: 'relative', width: '100%', height: 280, borderRadius: 10, overflow: 'hidden', backgroundColor: '#111', flexShrink: 0 }}>
                  <img
                    key={fotoIdx}
                    src={proxyFoto(fotos[fotoIdx])}
                    alt=""
                    onLoad={() => setLoaded(true)}
                    onError={e => { e.target.src = 'https://via.placeholder.com/800x450?text=Sin+imagen'; setLoaded(true); }}
                    style={{ width: '100%', height: '100%', objectFit: 'contain', opacity: loaded ? 1 : 0, transition: 'opacity 0.15s ease' }}
                  />
                  {!loaded && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={{ width: 24, height: 24, border: '3px solid rgba(255,255,255,0.2)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                    </div>
                  )}
                  {fotos.length > 1 && (
                    <>
                      <button onClick={() => { setLoaded(false); setFotoIdx(i => (i - 1 + fotos.length) % fotos.length); }}
                        style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', padding: '6px 10px', backgroundColor: 'rgba(0,0,0,0.55)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 18 }}>‹</button>
                      <button onClick={() => { setLoaded(false); setFotoIdx(i => (i + 1) % fotos.length); }}
                        style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', padding: '6px 10px', backgroundColor: 'rgba(0,0,0,0.55)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 18 }}>›</button>
                      <div style={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', backgroundColor: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 11, padding: '2px 8px', borderRadius: 20 }}>
                        {fotoIdx + 1} / {fotos.length}
                      </div>
                    </>
                  )}
                </div>
                {fotos.length > 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, overflowX: 'auto', overflowY: 'hidden', height: 72, flexShrink: 0 }}>
                    {fotos.map((f, i) => (
                      <button key={i} onClick={() => { setLoaded(false); setFotoIdx(i); }}
                        style={{ boxSizing: 'border-box', flexShrink: 0, width: 80, height: 60, borderRadius: 5, overflow: 'hidden', border: `2px solid ${i === fotoIdx ? t.accentBlue : 'transparent'}`, opacity: i === fotoIdx ? 1 : 0.55, cursor: 'pointer', padding: 0, backgroundColor: '#111' }}>
                        <img src={proxyFoto(f)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={e => { e.target.style.display = 'none'; }} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ height: 280, borderRadius: 10, backgroundColor: t.bgPanel, display: 'flex', alignItems: 'center', justifyContent: 'center', color: t.textMuted, fontSize: 13, flexShrink: 0 }}>Sin fotos</div>
            )}

            <div>
              <p style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Descripción</p>
              <p style={{ fontSize: 12, lineHeight: 1.6, color: t.textSecondary }}>{display.descripcion || 'Sin descripción disponible.'}</p>
            </div>
          </div>

          {/* Col derecha: la columna entera hace scroll si no cabe; el Historial (alto fijo) siempre queda en el flujo, nunca se pierde */}
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, overflowY: 'auto', overflowX: 'hidden' }}>
          <div style={{ padding: '20px 20px 0', display: 'flex', flexDirection: 'column', gap: 14, flexShrink: 0 }}>

            {/* Precio + Portales (estrecho, vertical, junto al precio — como en el diseño original) */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {display.precio_a_consultar && !display.precio_numerico ? (
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#f59e0b' }}>A consultar</div>
                ) : display.precio_numerico ? (
                  <div style={{ fontSize: 26, fontWeight: 800, color: '#22c55e' }}>{fmtPrecio(display.precio_numerico)}</div>
                ) : display.precio ? (
                  <div style={{ fontSize: 22, fontWeight: 800, color: t.textSecondary }}>{display.precio}</div>
                ) : null}

                {/* Contacto — debajo del precio, en la misma columna que los portales */}
                <div style={{ backgroundColor: t.bgPanel, borderRadius: 10, padding: 14 }}>
                  <p style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Contacto</p>
                  {[
                    [esAgenciaAnuncio ? 'Anunciante' : 'Propietario', nombre || '—', null],
                    ['Teléfono',    display.telefonos?.[0] || '—', tel ? 'call' : null, telLabel],
                    ['Email',       display.emails?.[0] || '—',    display.emails?.[0]    ? `mailto:${display.emails[0]}` : null],
                    ['Referencia',  display.referencia || '—',     null],
                  ].map(([k, v, href, labelOverride]) => (
                    <div key={k} style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: 6, marginBottom: 5 }}>
                      <span style={{ fontSize: 11, color: t.textMuted }}>{labelOverride || k}</span>
                      {href === 'call'
                        ? (
                          <button
                            type="button"
                            onClick={ejecutarLlamada}
                            disabled={llamando}
                            title="Llamar con Telnyx"
                            style={{
                              fontSize: 11, fontWeight: 600, color: '#ffffff', wordBreak: 'break-all',
                              textDecoration: 'underline', textDecorationColor: 'rgba(255,255,255,0.35)',
                              background: 'none', border: 'none', padding: 0, cursor: llamando ? 'wait' : 'pointer',
                              textAlign: 'left', fontFamily: 'inherit',
                            }}
                          >{v}</button>
                        )
                        : href
                          ? <a href={href} style={{ fontSize: 11, fontWeight: 600, color: k === 'Teléfono' ? '#ffffff' : t.accentBlue, wordBreak: 'break-all', textDecoration: 'none' }}>{v}</a>
                          : <span style={{ fontSize: 11, fontWeight: 600, color: k === 'Teléfono' ? '#ffffff' : t.textPrimary, wordBreak: 'break-all' }}>{v}</span>
                      }
                    </div>
                  ))}
                </div>
              </div>

              {hermanos.length >= 2 && (
                <div style={{ width: 138, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <p style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                    Portales · {hermanos.length}
                  </p>
                  {hermanos.map((a) => {
                    const esActual = String(a.id) === String(display.id);
                    const esParticular = String(a.tipo_anunciante || '').toLowerCase() === 'particular';
                    const contacto = esParticular
                      ? (a.nombre_contacto || 'Particular')
                      : (a.alias_agencia || a.nombre_contacto || 'Agencia');
                    const portalKey = String(a.portal || '').toLowerCase();
                    return (
                      <div
                        key={a.id}
                        role="button"
                        tabIndex={0}
                        onClick={(e) => selectAnuncio(a, e)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') selectAnuncio(a, e); }}
                        style={{
                          padding: '6px 8px', borderRadius: 8, cursor: 'pointer',
                          border: `1.5px solid ${esActual ? '#22c55e' : (t.borderLight || t.border)}`,
                          backgroundColor: esActual ? 'rgba(34,197,94,0.12)' : t.bgPanel,
                          display: 'flex', flexDirection: 'column', gap: 3,
                        }}
                      >
                        <span style={{
                          alignSelf: 'flex-start', padding: '1px 6px', borderRadius: 5, fontSize: 9, fontWeight: 700, color: '#fff',
                          background: PORTAL_COLOR[portalKey] || '#64748b',
                        }}>
                          {portalLabel(a.portal)}
                        </span>
                        <span style={{ fontSize: 9, color: t.textSecondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {contacto}
                        </span>
                        {a.precio_numerico > 0 ? (
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#22c55e' }}>{fmtPrecio(a.precio_numerico)}</span>
                        ) : a.precio === 'A consultar' ? (
                          <span style={{ fontSize: 10, fontWeight: 700, color: '#f59e0b' }}>A consultar</span>
                        ) : null}
                        {a.url_original && (
                          <a href={a.url_original} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                            style={{ fontSize: 9, fontWeight: 600, color: '#fff', textDecoration: 'underline' }}>
                            Ver ↗
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Acciones rápidas de la captación (WhatsApp / Llamada / Nota) */}
            {captacionPipeline && <div ref={setAccionesEl} />}

            {/* Propiedad + Extras + Localización: un único contenedor, en tres columnas para que quepa más sin agrandarlo */}
            <div style={{ backgroundColor: t.bgPanel, borderRadius: 10, padding: 14 }}>
              <p style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Propiedad</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px 10px' }}>
                {[
                  ['Operación',    car.operacion || null],
                  ['Tipo',        traducirTipo(car.tipo_propiedad || display.tipo_inmueble)],
                  ['Habitaciones', car.habitaciones ?? display.habitaciones ?? null],
                  ['Baños',        car.banos        ?? display.banos        ?? null],
                  ['Superficie',   car.superficie_m2      ? `${car.superficie_m2} m²`      : display.superficie_m2 ? `${display.superficie_m2} m²` : null],
                  ['Sup. útil',    car.superficie_util    ? `${car.superficie_util} m²`    : null],
                  ['Parcela',      car.superficie_parcela_m2 ? `${car.superficie_parcela_m2} m²` : null],
                  ['Planta',       car.planta || display.planta || null],
                  ['Tipo suelo',   car.tipo_suelo ?? null],
                  ['Orientación',  display.orientacion || car.orientacion || null],
                  ['Energía',      car.certificacion_energetica || car.certificado_energia || null],
                  ['Población',    display.municipio || null],
                  ['Provincia',    display.provincia || null],
                  ['C.P.',         display.codigo_postal || null],
                ].filter(([, v]) => v !== null && v !== undefined && v !== '').map(([k, v]) => (
                  <div key={k}>
                    <div style={{ fontSize: 9, color: t.textMuted, marginBottom: 1 }}>{k}</div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: t.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{String(v)}</div>
                  </div>
                ))}
              </div>

              {(() => {
                const EXTRAS_BOOL = [
                  ['piscina','🏊 Piscina'], ['garaje','🚗 Garaje'], ['terraza','🌿 Terraza'],
                  ['jardin','🌳 Jardín'], ['ascensor','🛗 Ascensor'], ['amueblado','🪑 Amueblado'],
                  ['primera_linea','🏖️ 1ª línea'], ['vista_mar','🌊 Vista mar'],
                  ['vistas_mar','🌊 Vista mar'], ['pista_padel','🎾 Pádel'],
                  ['aire_acondicionado','❄️ Aire acond.'], ['trastero','📦 Trastero'],
                  ['calefaccion','🔥 Calefacción'],
                  ['agua','💧 Agua'], ['gas','🔥 Gas'], ['luz','💡 Luz'],
                  ['telefono_vivienda','📞 Tel. vivienda'], ['chimenea','🕯️ Chimenea'],
                ];
                // Deduplicar (vista_mar y vistas_mar son el mismo)
                const seen = new Set();
                const activos = EXTRAS_BOOL.filter(([k, label]) => {
                  if ((car[k] === true || car[k] === 'true') && !seen.has(label)) {
                    seen.add(label); return true;
                  }
                  return false;
                });
                const extrasFotocasa = (car.todos_extras || []).map(ex => traducirExtra(ex));
                const chips = [...activos.map(([, label]) => label), ...extrasFotocasa];
                if (!chips.length) return null;
                return (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 10, paddingTop: 10, borderTop: `1px solid ${t.borderLight || t.border}` }}>
                    {chips.map((label, i) => (
                      <span key={i} style={{ padding: '3px 8px', borderRadius: 6, fontSize: 11, backgroundColor: t.chipBg || '#dbeafe', color: t.chipText || '#1d4ed8', border: `1px solid ${t.chipBorder || '#bfdbfe'}` }}>{label}</span>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Historial: alto fijo dentro del flujo normal de la columna, siempre presente; título/acciones fijos y solo la lista hace scroll */}
          <div ref={setHistorialEl} style={{ height: 240, flexShrink: 0, marginTop: 14, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderTop: `1px solid ${t.border}` }} />

          {/* Acciones manuales (llamar/whatsapp/email) */}
          {((tel && (verScrLlamar || verScrWhatsapp)) || (email && verScrEmail)) && (
            <div style={{ padding: '0 20px 20px', flexShrink: 0 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                {tel && (
                  <>
                    {verScrLlamar && (
                    <button
                      type="button"
                      onClick={ejecutarLlamada}
                      disabled={llamando}
                      style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '9px 0', background: t.btnCallBg || '#22c55e', color: '#fff', border: 'none', borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: llamando ? 'wait' : 'pointer', fontFamily: 'inherit', opacity: llamando ? 0.75 : 1 }}
                    >
                      📞 {llamando ? 'Llamando…' : 'Llamar'}
                    </button>
                    )}
                    {verScrWhatsapp && (
                    <button type="button" onClick={() => setModalWa(true)}
                      style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '9px 0', background: t.btnWhatsappBg || '#25d366', color: '#fff', border: 'none', borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                      💬 WhatsApp
                    </button>
                    )}
                  </>
                )}
                {email && verScrEmail && (
                  <button onClick={() => setModalEmail(true)}
                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '9px 0', backgroundColor: t.accentBlue, color: '#fff', border: 'none', borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    ✉️ Email
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
        </div>

        {onFocusVincular && verScrCoincidencias && (
          <div style={{
            flexShrink: 0, borderTop: `1px solid ${t.border}`,
            padding: "10px 20px", backgroundColor: t.bgPanel,
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
          }}>
            <span style={{ fontSize: 10, color: t.textMuted }}>
              {display.id_propiedad_mercado ? "Grupo asignado" : "Sin grupo de vivienda"}
            </span>
            <button type="button" onClick={() => { onFocusVincular(display); onClose(); }} style={{
              padding: "6px 12px", borderRadius: 8, fontSize: 11, fontWeight: 600,
              border: `1px solid ${t.border}`, background: t.bgCard, color: t.textPrimary,
              cursor: "pointer", fontFamily: "inherit",
            }}>
              🔗 Buscar coincidencias →
            </button>
          </div>
        )}

        </div>

        {captacionPipeline && (
          <PanelHistorialCaptacion
            captacion={captacionPipeline}
            isDark={isDark}
            c={t}
            layout="split"
            ocultarTareaPendiente
            accionesTarget={accionesEl}
            historialTarget={historialEl}
            onAvanzado={() => { onUpdated?.(); onClose?.(); }}
          />
        )}

        {captacionPipeline && (
          <div style={{ flexShrink: 0, borderTop: `1px solid ${t.border}` }}>
            {accionArchivo && (
              <div style={{
                padding: "14px 20px",
                borderBottom: `1px solid ${t.border}`,
                background: accionArchivo === "frio"
                  ? (isDark ? "rgba(59,130,246,0.08)" : "#eff6ff")
                  : (isDark ? "rgba(239,68,68,0.08)" : "#fef2f2"),
              }}>
                <div style={{
                  fontSize: 12, fontWeight: 700, marginBottom: 8,
                  color: accionArchivo === "frio" ? "#3b82f6" : "#ef4444",
                }}>
                  {accionArchivo === "frio" ? "❄️ Marcar como frío" : "🗑️ Eliminar precaptación"} — Motivo (opcional)
                </div>
                <textarea
                  value={motivoArchivo}
                  onChange={e => setMotivoArchivo(e.target.value)}
                  placeholder="Ej: No contesta, ya tiene agencia, precio irreal..."
                  rows={2}
                  style={inputArchivoStyle}
                />
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <button
                    type="button"
                    onClick={() => { setAccionArchivo(null); setMotivoArchivo(""); }}
                    style={{
                      flex: 1, padding: "9px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                      border: `1px solid ${t.border}`, background: "transparent", color: t.textMuted,
                      cursor: "pointer", fontFamily: "inherit",
                    }}
                  >Cancelar</button>
                  <button
                    type="button"
                    onClick={handleArchivar}
                    disabled={archivando}
                    style={{
                      flex: 2, padding: "9px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700,
                      border: "none", cursor: archivando ? "not-allowed" : "pointer", fontFamily: "inherit",
                      background: accionArchivo === "frio" ? "#3b82f6" : "#ef4444", color: "#fff",
                      opacity: archivando ? 0.6 : 1,
                    }}
                  >
                    {archivando ? "Procesando..." : accionArchivo === "frio" ? "❄️ Confirmar frío" : "🗑️ Confirmar eliminación"}
                  </button>
                </div>
              </div>
            )}

            {!accionArchivo && (
              <div style={{
                padding: "12px 20px",
                display: "flex",
                gap: 8,
                alignItems: "center",
                background: isDark ? "transparent" : "#e0e0d8",
              }}>
                <button
                  type="button"
                  onClick={() => { setAccionArchivo("frio"); setMotivoArchivo(""); }}
                  style={{
                    padding: "9px 14px", borderRadius: 8, fontSize: 11, fontWeight: 600,
                    border: "1px solid #3b82f640", background: isDark ? "rgba(59,130,246,0.08)" : "#eff6ff",
                    color: "#3b82f6", cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
                  }}
                >❄️ Frío</button>
                <button
                  type="button"
                  onClick={() => { setAccionArchivo("eliminado"); setMotivoArchivo(""); }}
                  style={{
                    padding: "9px 14px", borderRadius: 8, fontSize: 11, fontWeight: 600,
                    border: "1px solid #ef444440", background: isDark ? "rgba(239,68,68,0.08)" : "#fef2f2",
                    color: "#ef4444", cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
                  }}
                >🗑️ Eliminar</button>
              </div>
            )}
          </div>
        )}
      </div>

      {modalWa && (
        <ModalWhatsApp
          isOpen={modalWa}
          contact={contactoModal}
          onClose={() => setModalWa(false)}
          onSend={() => setModalWa(false)}
        />
      )}
      {modalEmail && (
        <ModalEmail
          isOpen={modalEmail}
          contact={contactoModal}
          onClose={() => setModalEmail(false)}
          onSend={() => setModalEmail(false)}
        />
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

/** En Pipeline (captacion vinculada) → modal de precaptación; en MarketPlace → ficha de anuncio. */
export default function ModalDetallePropiedad(props) {
  if (props.captacion?.id) {
    return (
      <ModalNuevaPrecaptacion
        isOpen
        captacion={props.captacion}
        onClose={props.onClose}
        onCreated={() => { props.onUpdated?.(); props.onClose?.(); }}
      />
    );
  }
  return <ModalDetallePropiedadScraping {...props} />;
}
